import Skeleton from "./ui/Skeleton";
import "./TabSkeleton.css";

/**
 * Reusable Tab Skeleton for sub-views like Analytics, Search Console, Knowledge Engine, etc.
 */
export function TabSkeleton({ type = "default" }) {
  if (type === "analytics" || type === "search-console") {
    return (
      <div className="tab-skeleton-container" aria-label="Loading analytics data...">
        {/* Header / Date filter */}
        <div className="tab-skeleton-header">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton style={{ width: 220, height: 24, borderRadius: "6px" }} />
            <Skeleton style={{ width: 340, height: 14 }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Skeleton style={{ width: 150, height: 38, borderRadius: "8px" }} />
            <Skeleton style={{ width: 120, height: 38, borderRadius: "8px" }} />
          </div>
        </div>

        {/* 4 Stats Cards */}
        <div className="tab-skeleton-cards-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="tab-skeleton-card">
              <Skeleton style={{ width: 110, height: 14 }} />
              <Skeleton style={{ width: 85, height: 28, borderRadius: "6px" }} />
              <Skeleton style={{ width: 60, height: 12 }} />
            </div>
          ))}
        </div>

        {/* Big Chart Area */}
        <div className="tab-skeleton-chart-box">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Skeleton style={{ width: 180, height: 18 }} />
            <Skeleton style={{ width: 110, height: 28, borderRadius: "6px" }} />
          </div>
          <Skeleton style={{ width: "100%", height: 200, borderRadius: "8px" }} />
        </div>

        {/* Breakdown Table */}
        <div className="tab-skeleton-table-box">
          <Skeleton style={{ width: 160, height: 18, marginBottom: 6 }} />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="tab-skeleton-row">
              <Skeleton style={{ width: "40%", height: 14 }} />
              <Skeleton style={{ width: "20%", height: 14 }} />
              <Skeleton style={{ width: "15%", height: 14 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "knowledge") {
    return (
      <div className="tab-skeleton-container" aria-label="Loading AI Knowledge Engine...">
        <div className="tab-skeleton-header">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton style={{ width: 260, height: 24, borderRadius: "6px" }} />
            <Skeleton style={{ width: 400, height: 14 }} />
          </div>
          <Skeleton style={{ width: 140, height: 38, borderRadius: "8px" }} />
        </div>

        {/* 3 cards: Schema, llms.txt, robots.txt */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="tab-skeleton-card" style={{ minHeight: 180 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Skeleton style={{ width: 120, height: 16 }} />
                <Skeleton style={{ width: 30, height: 30, borderRadius: "8px" }} />
              </div>
              <Skeleton style={{ width: "90%", height: 12 }} />
              <Skeleton style={{ width: "70%", height: 12 }} />
              <div style={{ marginTop: "auto", display: "flex", gap: 8 }}>
                <Skeleton style={{ width: 80, height: 28, borderRadius: "6px" }} />
                <Skeleton style={{ width: 80, height: 28, borderRadius: "6px" }} />
              </div>
            </div>
          ))}
        </div>

        {/* Code / Configuration viewer preview shimmer */}
        <div className="tab-skeleton-chart-box" style={{ minHeight: 320 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Skeleton style={{ width: 220, height: 18 }} />
            <Skeleton style={{ width: 90, height: 28, borderRadius: "6px" }} />
          </div>
          <Skeleton style={{ width: "100%", height: 240, borderRadius: "8px" }} />
        </div>
      </div>
    );
  }

  // Default / Linking / Monthly Report / Help
  return (
    <div className="tab-skeleton-container" aria-label="Loading content...">
      <div className="tab-skeleton-header">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Skeleton style={{ width: 240, height: 24, borderRadius: "6px" }} />
          <Skeleton style={{ width: 350, height: 14 }} />
        </div>
        <Skeleton style={{ width: 130, height: 38, borderRadius: "8px" }} />
      </div>

      <div className="tab-skeleton-cards-grid">
        {[1, 2, 3].map((i) => (
          <div key={i} className="tab-skeleton-card">
            <Skeleton style={{ width: 100, height: 14 }} />
            <Skeleton style={{ width: 80, height: 26, borderRadius: "6px" }} />
            <Skeleton style={{ width: "80%", height: 12 }} />
          </div>
        ))}
      </div>

      <div className="tab-skeleton-table-box">
        <Skeleton style={{ width: 180, height: 20, marginBottom: 8 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="tab-skeleton-row">
            <div style={{ display: "flex", alignItems: "center", gap: 12, width: "60%" }}>
              <Skeleton style={{ width: 24, height: 24, borderRadius: "6px" }} />
              <Skeleton style={{ width: "80%", height: 14 }} />
            </div>
            <Skeleton style={{ width: 80, height: 22, borderRadius: "12px" }} />
            <Skeleton style={{ width: 90, height: 30, borderRadius: "6px" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default TabSkeleton;
